import { apiRequest } from "../../api/apiRequest";
import {
  GET_ALL_SESSIONS,
  GET_SESSION_DATA,
  POST_AIRA_SPEAKER,
  POST_CHAT_HISTORY,
  POST_RAG_UPLOAD,
  POST_TYPE_CHAT,
} from "../../utils/api";
import {
  TYPE_CHAT_STREAM_START,
  TYPE_CHAT_STREAM_UPDATE,
  TYPE_CHAT_SUCCESS,
  TYPE_CHAT_FAILURE,
  TYPE_CHAT_CLEAR,
  CHAT_HISTORY_ONE_SUCCESS,
  CHAT_HISTORY_ONE_FAILURE,
  CHAT_HISTORY_SEVEN_SUCCESS,
  CHAT_HISTORY_SEVEN_FAILURE,
  CHAT_HISTORY_MONTH_SUCCESS,
  CHAT_HISTORY_MONTH_FAILURE,
  ANSWER_SPEAKER_SUCCESS,
  ANSWER_SPEAKER_FAILURE,
  TYPE_CHAT_STREAM_META,
  RAG_UPLOAD_SUCCESS,
  RAG_UPLOAD_FAILURE,
  GET_SESSION_DATA_SUCCESS,
  GET_SESSION_DATA_FAILURE,
  GET_ALL_SESSIONS_SUCCESS,
  GET_ALL_SESSIONS_FAILURE,
} from "./chat.actionTypes";

const META_EVENT_NAMES = new Set(["done", "meta", "end"]);
const ERROR_EVENT_NAMES = new Set(["error"]);

// const safeStorage = {
//   get(key) {
//     try {
//       return localStorage.getItem(key);
//     } catch (e) {
//       return null;
//     }
//   },
//   set(key, value) {
//     try {
//       localStorage.setItem(key, value);
//     } catch (e) {
//       console.warn(`Unable to persist "${key}" to localStorage`, e);
//     }
//   },
// };

function safeJsonParse(str) {
  try {
    return JSON.parse(str);
  } catch (e) {
    return undefined;
  }
}

function parseSSEEventBlock(block) {
  let eventType = "message";
  const dataLines = [];

  for (const rawLine of block.split(/\r?\n/)) {
    if (!rawLine || rawLine.startsWith(":")) continue; // blank / heartbeat comment

    if (rawLine.startsWith("event:")) {
      eventType = rawLine.slice(6).trim() || "message";
    } else if (rawLine.startsWith("data:")) {
      dataLines.push(rawLine.slice(5).trimStart());
    }
  }

  return { eventType, data: dataLines.join("\n").trim() };
}

function decodeMessageToken(data) {
  if (!data || data === "[DONE]") return "";

  const parsed = safeJsonParse(data);

  if (parsed === undefined) {
    // Not valid JSON - backend sent raw text, use as-is.
    return data;
  }
  if (typeof parsed === "string") {
    return parsed;
  }
  if (parsed && typeof parsed === "object") {
    return parsed.token ?? parsed.content ?? parsed.text ?? parsed.delta ?? "";
  }
  return String(parsed);
}

export const generateChat =
  (payload, { signal } = {}) =>
  async (dispatch) => {
   const token = localStorage.getItem("access_token") || null;

    dispatch({
      type: TYPE_CHAT_STREAM_START,
      payload: { threadId: payload.thread_id },
    });

    let displayText = "";
    let parsedQuestion = "";
    let updatedHistory = [];
    let threadId = payload.thread_id || null;

    // rAF batching so Redux doesn't re-render on every single token
    let pendingText = "";
    let rafId = null;

    const flush = () => {
      if (pendingText) {
        dispatch({
          type: TYPE_CHAT_STREAM_UPDATE,
          payload: { chunk: pendingText },
        });
        pendingText = "";
      }
      rafId = null;
    };

    const queueUpdate = (text) => {
      if (!text) return;
      pendingText += text;
      if (!rafId) rafId = requestAnimationFrame(flush);
    };

    const cancelPendingFlush = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const applyMeta = (obj) => {
      if (!obj) return;
      if (obj.question !== undefined) parsedQuestion = obj.question || "";
      if (Array.isArray(obj.messages)) updatedHistory = obj.messages;
      if (obj.thread_id || obj.session_id) {
        threadId = obj.thread_id || obj.session_id || threadId;
      }
      // if (obj.session_id) safeStorage.set("session_id", obj.session_id);

      dispatch({
        type: TYPE_CHAT_STREAM_META,
        payload: {
          threadId,
          history: updatedHistory,
          question: parsedQuestion,
        },
      });
    };

    const processEvent = (rawEvent) => {
      if (!rawEvent || !rawEvent.trim()) return;

      const { eventType, data } = parseSSEEventBlock(rawEvent);
      if (!data) return;

      if (ERROR_EVENT_NAMES.has(eventType)) {
        const parsed = safeJsonParse(data);
        const msg = (parsed && (parsed.message || parsed.error)) || data;
        throw new Error(msg || "The server reported a streaming error.");
      }

      if (META_EVENT_NAMES.has(eventType)) {
        const metaObj = safeJsonParse(data);
        if (metaObj) applyMeta(metaObj);
        return;
      }

      // Default: "message" event - a text token
      const text = decodeMessageToken(data);
      if (text) {
        displayText += text;
        queueUpdate(text);
      }
    };

    // ---- build request body ----
    const hasAudio = !!payload.mic_audio_file;

    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "text/event-stream",
    };

    let body;
    if (hasAudio) {
      // multipart: a File can't be sent as JSON
      body = new FormData();
      body.append("mic_audio", "true");
      body.append("mic_audio_file", payload.mic_audio_file);
      body.append("message", payload.message || "");
      if (payload.thread_id) body.append("thread_id", payload.thread_id);
      if (payload.documentID) body.append("document_id", payload.documentID);
      // don't set Content-Type: the browser adds it with the multipart boundary
    } else {
      body = new FormData();
      body.append("message", payload.message || "");
      if (payload.thread_id) body.append("thread_id", payload.thread_id);
      if (payload.documentID) body.append("document_id", payload.documentID);
      body.append("mic_audio", "false");
      // don't set Content-Type: the browser adds it with the multipart boundary
    }

    let reader;

    try {
      let response;
      try {
        response = await fetch(POST_TYPE_CHAT, {
          method: "POST",
          headers,
          credentials: "include",
          body,
          signal,
        });
      } catch (networkError) {
        if (networkError?.name === "AbortError") throw networkError;
        throw new Error(
          "Network error - please check your connection and try again.",
        );
      }

      if (!response.ok) {
        let serverMessage = "";
        try {
          const errBody = await response.clone().json();
          serverMessage = errBody?.message || errBody?.error || "";
        } catch (e) {
          try {
            serverMessage = await response.clone().text();
          } catch (e2) {
            /* ignore */
          }
        }
        throw new Error(
          serverMessage || `Request failed with status ${response.status}`,
        );
      }

      if (!response.body || typeof response.body.getReader !== "function") {
        // Streaming fetch unsupported - fall back to reading it whole.
        const fullText = await response.text();
        for (const block of fullText.split(/\r?\n\r?\n/)) {
          processEvent(block);
        }
      } else {
        reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
          let readResult;
          try {
            readResult = await reader.read();
          } catch (streamErr) {
            if (streamErr?.name === "AbortError") throw streamErr;
            throw new Error("Connection lost while receiving the response.");
          }

          const { value, done } = readResult;
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          let boundary;
          while ((boundary = buffer.indexOf("\n\n")) !== -1) {
            const rawEvent = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            processEvent(rawEvent);
          }
        }

        buffer += decoder.decode(); // flush trailing decoder bytes
        if (buffer.trim()) processEvent(buffer);
      }

      cancelPendingFlush();
      flush();

      dispatch({
        type: TYPE_CHAT_SUCCESS,
        payload: {
          data: displayText,
          history: updatedHistory,
          questionData: parsedQuestion,
          threadId,
        },
      });

      await dispatch(getSessionData(threadId));

      return { success: true, threadId, data: displayText };
    } catch (error) {
      cancelPendingFlush();
      try {
        reader?.cancel();
      } catch (e) {
        /* already closed - ignore */
      }

      if (error?.name === "AbortError") {
        return { success: false, aborted: true };
      }

      const errMsg = error?.message || "Something went wrong";

      dispatch({
        type: TYPE_CHAT_FAILURE,
        payload: { message: errMsg, partialData: displayText },
      });

      return { success: false, message: errMsg };
    }
  };

export const clearChat = () => ({
  type: TYPE_CHAT_CLEAR,
});

export const ragDocumentUpload = (payload) => async (dispatch) => {
  const formData = new FormData();
  formData.append("file", payload?.file);

  const result = await apiRequest("post", POST_RAG_UPLOAD, formData);

  if (result.success) {
    dispatch({
      type: RAG_UPLOAD_SUCCESS,
      payload: { data: result.data },
    });
    return { success: true, data: result?.data };
  }

  dispatch({
    type: RAG_UPLOAD_FAILURE,
    payload: { message: result.message },
  });
  return { success: false, message: result.message };
};

export const getSessionData = (threadID) => async (dispatch) => {
  const result = await apiRequest("get", `${GET_SESSION_DATA}/${threadID}`);

  if (result.success) {
    dispatch({
      type: GET_SESSION_DATA_SUCCESS,
      payload: { data: result.data },
    });
    return { success: true };
  }

  dispatch({
    type: GET_SESSION_DATA_FAILURE,
    payload: { message: result.message },
  });
  return { success: false, message: result.message };
};

export const allSessionsData = (threadID) => async (dispatch) => {
  const result = await apiRequest("get", `${GET_ALL_SESSIONS}`);

  if (result.success) {
    dispatch({
      type: GET_ALL_SESSIONS_SUCCESS,
      payload: { data: result.data },
    });
    return { success: true };
  }

  dispatch({
    type: GET_ALL_SESSIONS_FAILURE,
    payload: { message: result.message },
  });
  return { success: false, message: result.message };
};
